import { SxProps, Theme } from '@mui/material/styles';

import React, { useEffect, useState } from 'react';

import { AiCallForCallPopDTO, Company, isPoolBrainCompany } from '_lib/api/apiTypes.shim';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { Stack } from '_lib/components/Stack/Stack';

import { TabPanel } from '_lib/components/TabPanel/TabPanel';
import { ThreeTabFooter } from '_lib/featureComponents/ThreeTabFooter/ThreeTabFooter';
import { TwoTabFooter } from '_lib/featureComponents/TwoTabFooter/TwoTabFooter';
import { useMediaQuery } from '_lib/hooks/useMediaQuery';
import { useAppDispatch } from '_lib/redux/hooks';
import { VerticalDivider } from '_lib/styles/PhoneCall.styled';
import { PhoneCallURLState } from '_lib/types/PhoneCall.types';
import { logDebug } from '_lib/utils/logging';

import { PoolBrainCompanyContext } from '../../common/CompanyPane/Company.slice';
import { CompanyPane } from '../../common/CompanyPane/CompanyPane';

import { CompanyZoneZipSelect } from './featureComponents/CompanyPane/CompanyZoneZipSelect/CompanyZoneZipSelect';
import { CustomerLocationPane } from './featureComponents/CustomerLocationPane/CustomerLocationPane';
import { PoolBrainCallDetails } from './featureComponents/PoolBrainCallDetails/PoolBrainCallDetails';
import { normalizeTradeFromCompany } from './PoolBrainPhoneCall.slice';

type PoolBrainPhoneCallProps = {
  companyContext: PoolBrainCompanyContext;
  phoneCallURLState: PhoneCallURLState;
  aiCall: AiCallForCallPopDTO['aiCall'] | null;
  handleSwitchCompany: (companyId: string) => Promise<Company | null>;
};

export const PoolBrainPhoneCall = (props: PoolBrainPhoneCallProps): React.ReactElement => {
  const { companyContext, phoneCallURLState, aiCall, handleSwitchCompany } = props;

  const dispatch = useAppDispatch();

  const handleSwitchPoolBrainCompany = async (companyId: string) => {
    logDebug('// - Company Switch Handler PoolBrain - //');
    const newCompany = await handleSwitchCompany(companyId);
    if (!newCompany || !isPoolBrainCompany(newCompany)) return;
    dispatch(normalizeTradeFromCompany(newCompany));
  };

  const [tabIndex, setTabIndex] = useState(1); // default to customer pane shown if in tabbed view
  const [isSaving, setIsSaving] = useState(false);

  // If 'lg' or smaller, render with customer and company tabs
  const twoTabView = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  // If 'md' or smaller, customer, call, and company are all tabbed
  const threeTabView = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const generateCallDetailsTabSx = (): SxProps<Theme> => {
    if (threeTabView) return { width: '100%', height: '100%' };
    if (twoTabView) return { width: '30%', minWidth: '330px', height: '100%' };
    return { width: '30%', height: '100%', minWidth: '330px' };
  };

  const generateCustomerTabSx = (): SxProps<Theme> => {
    if (threeTabView) return { width: '100%', height: '100%' };
    if (twoTabView) return { width: '70%', height: '100%' };
    return { width: '30%', height: '100%' };
  };

  const generateCompanyTabSx = (): SxProps<Theme> => {
    if (threeTabView) return { width: '100%', height: '100%' };
    if (twoTabView) return { width: '70%', height: '100%' };
    return { width: '40%', height: '100%' };
  };

  // We want to auto-select 'customer' if we had 'call' selected and then left the breakpoint width
  useEffect(() => {
    if (twoTabView && !threeTabView && tabIndex === 0) setTabIndex(1);
  }, [twoTabView, threeTabView, tabIndex]);

  return (
    <FlexBox sx={{ height: '100vh', width: '100%', flexDirection: 'column', overflow: 'hidden' }}>
      <BackdropSpinner open={isSaving} />
      <Stack direction={'row'} spacing={0} sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        <TabPanel currentIndex={tabIndex} index={0} alwaysDisplay={!threeTabView} sx={generateCallDetailsTabSx()}>
          <PoolBrainCallDetails
            companyContext={companyContext}
            phoneCallURLState={phoneCallURLState}
            aiCallId={aiCall?.id}
            onSetIsSaving={setIsSaving}
            showFooter={!twoTabView || threeTabView}
          />
        </TabPanel>
        <VerticalDivider />
        <TabPanel
          currentIndex={tabIndex}
          index={1}
          alwaysDisplay={!twoTabView && !threeTabView}
          sx={generateCustomerTabSx()}>
          <CustomerLocationPane phoneCallURLState={phoneCallURLState} company={companyContext.company} />
        </TabPanel>
        <VerticalDivider />
        <TabPanel
          currentIndex={tabIndex}
          index={2}
          alwaysDisplay={!twoTabView && !threeTabView}
          sx={generateCompanyTabSx()}>
          <CompanyPane
            zipZoneOptions={{ ZipZoneSelectComponent: CompanyZoneZipSelect }}
            companyContext={companyContext}
            phoneCallURLState={phoneCallURLState}
            aiCall={aiCall}
            handleSwitchCompany={handleSwitchPoolBrainCompany}
            sx={{ visibility: 'visible', width: '100%', height: '100%' }}
          />
        </TabPanel>
      </Stack>
      {twoTabView && !threeTabView && (
        <TwoTabFooter callDetailsWidth='30%' tabIndex={tabIndex} setTabIndex={setTabIndex} />
      )}
      {threeTabView && <ThreeTabFooter tabIndex={tabIndex} setTabIndex={setTabIndex} />}
    </FlexBox>
  );
};
